import { z } from 'zod';

import { CartAbandonmentModal, InterstitialModal } from '@/types/InterstitialModal';

export type ValidationErrors = Record<keyof FormData, string[]>;

export const SHOWINTERSTITIALMODAL = 'showInterstitialModal';
export const initialState = {
  firstName: '',
  lastName: '',
  email: '',
};

export const formSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().min(1, 'Email is required').email('Please enter a valid email address.'),
});

export type FormData = z.infer<typeof formSchema>;
export type FormErrors = Partial<Record<keyof FormData, string>>;

export const mapErrorFields = (res: { validationErrors: string | ValidationErrors }) => {
  const errorFields: FormErrors = {};

  const parsedErrors: ValidationErrors =
    typeof res.validationErrors === 'string' ? JSON.parse(res.validationErrors) : res.validationErrors;

  Object.entries(parsedErrors).forEach(([key, messages]) => {
    errorFields[key as keyof FormData] = messages[0];
  });

  return errorFields;
};

// Webaliases where interstitial popup should NOT be shown
// Popup should show for ALL replicated sites EXCEPT these
export const blockedWebAliasesForInterstitialPopUp = new Set<string>(['pawtree', 'pawtreeshop']);

/**
 * Check if interstitial popup should be shown for a given webalias
 * Returns true for all webaliases EXCEPT pawtree and pawtreeshop
 */
export const shouldShowInterstitialForWebalias = (webalias: string): boolean => {
  return !blockedWebAliasesForInterstitialPopUp.has(webalias.toLowerCase());
};

/**
 * Check if the popup is within valid date range (between startDate and endDate)
 * Returns true if current date is within the valid range
 */
export const isPopupWithinValidDateRange = (startDate?: string, endDate?: string): boolean => {
  // If no dates provided, allow the popup to show
  if (!startDate && !endDate) return true;

  const now = new Date();
  // Set time to start of day for comparison (ignore time component)
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  const getLocalDate = (dateStr: string) => {
    // Handle YYYY-MM-DD format manually to avoid UTC parsing
    const datePart = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
    const parts = datePart.split('-');

    if (parts.length === 3) {
      const [year, month, day] = parts.map(Number);
      // Create date in local timezone (month is 0-indexed)
      return new Date(year, month - 1, day);
    }

    // Fallback for other formats (will rely on browser parsing)
    const d = new Date(dateStr);
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  };

  // Check startDate - popup should not show if today is before startDate
  if (startDate) {
    const startDay = getLocalDate(startDate);
    if (today < startDay) return false;
  }

  // Check endDate - popup should not show if today is after endDate
  if (endDate) {
    const endDay = getLocalDate(endDate);
    if (today > endDay) return false;
  }

  return true;
};

export const normalizeCartAbandonmentModalsData = (data: CartAbandonmentModal): InterstitialModal => {
  return {
    ...data,
    popupName: data.name,
    timeDelay: 3000,
    welcomeBackPopup: data.greatToSeeYouAgainPopup,
    publishedAt: null,
  };
};
